import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from "@/components/ui/tabs";
import { CheckCircle2, XCircle } from "lucide-react";
import { toast } from "sonner";

function StatusBadge({ s }) {
  const map = { approved: "badge-paid", pending: "badge-due-today", rejected: "badge-overdue" };
  return (
    <span className={`text-[10px] font-bold tracking-wider px-2 py-0.5 rounded-sm ${map[s] || "badge-unpaid"}`}>
      {s.toUpperCase()}
    </span>
  );
}

function PaymentsTable({ rows, onApprove, onReject, showActions }) {
  return (
    <div className="glass-card rounded-lg overflow-hidden">
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Submitted</TableHead>
              <TableHead>User</TableHead>
              <TableHead>Plan</TableHead>
              <TableHead>Method</TableHead>
              <TableHead>TXN ID</TableHead>
              <TableHead>Payer #</TableHead>
              <TableHead className="text-right">Amount</TableHead>
              <TableHead>Status</TableHead>
              {showActions && <TableHead></TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={showActions ? 9 : 8} className="text-center py-8 text-muted-foreground">
                  No payments.
                </TableCell>
              </TableRow>
            )}
            {rows.map((p) => (
              <TableRow key={p.id} data-testid={`pay-${p.id}`}>
                <TableCell className="font-mono text-xs">{p.submitted_at?.slice(0, 10)}</TableCell>
                <TableCell>
                  <div className="font-medium">{p.user?.name || "—"}</div>
                  <div className="text-xs text-muted-foreground font-mono">{p.user?.email}</div>
                </TableCell>
                <TableCell>{p.plan?.name || "—"}</TableCell>
                <TableCell className="uppercase text-xs font-bold">{p.method}</TableCell>
                <TableCell className="font-mono text-xs">{p.txn_id}</TableCell>
                <TableCell className="font-mono text-xs">{p.payer_number || "—"}</TableCell>
                <TableCell className="text-right font-mono font-semibold">৳{Number(p.amount).toFixed(0)}</TableCell>
                <TableCell><StatusBadge s={p.status} /></TableCell>
                {showActions && (
                  <TableCell className="text-right whitespace-nowrap">
                    <Button size="sm" onClick={() => onApprove(p.id)} data-testid={`approve-${p.id}`} className="mr-1">
                      <CheckCircle2 className="w-4 h-4 mr-1" /> Approve
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => onReject(p.id)} data-testid={`reject-${p.id}`} className="text-red-500">
                      <XCircle className="w-4 h-4 mr-1" /> Reject
                    </Button>
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

export default function AdminPayments() {
  const [pending, setPending] = useState([]);
  const [all, setAll] = useState([]);

  const load = async () => {
    const [{ data: p }, { data: a }] = await Promise.all([
      api.get("/admin/payments", { params: { status: "pending" } }),
      api.get("/admin/payments"),
    ]);
    setPending(p);
    setAll(a);
  };
  useEffect(() => { load(); }, []);

  const approve = async (id) => {
    try {
      await api.post(`/admin/payments/${id}/approve`);
      toast.success("Approved · subscription activated");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    }
  };

  const reject = async (id) => {
    if (!window.confirm("Reject this payment?")) return;
    try {
      await api.post(`/admin/payments/${id}/reject`);
      toast.success("Rejected");
      load();
    } catch (e) {
      toast.error("Failed");
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-heading text-3xl sm:text-4xl font-black tracking-tight">Payments</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Approve pending payments to activate subscriptions.
        </p>
      </div>

      <Tabs defaultValue="pending">
        <TabsList>
          <TabsTrigger value="pending" data-testid="tab-pending">
            Pending ({pending.length})
          </TabsTrigger>
          <TabsTrigger value="all" data-testid="tab-all">All ({all.length})</TabsTrigger>
        </TabsList>
        <TabsContent value="pending" className="mt-4">
          <PaymentsTable rows={pending} onApprove={approve} onReject={reject} showActions />
        </TabsContent>
        <TabsContent value="all" className="mt-4">
          <PaymentsTable rows={all} onApprove={approve} onReject={reject} showActions={false} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
